// Point-and-click bug reports: pick an element on the page and capture structured context for
// the feedback pipeline. Pure DOM (no React) — the overlay is transient, must sit above every
// surface, and must never re-render the app while picking. Availability is gated in Feedback.tsx
// (admin always; everyone when the `feedback.bugPickerPublic` config is on).

export interface ElementContext {
  route: string;
  /** Component name — from a `data-c` ancestor, else inferred from the house class prefixes. */
  component: string | null;
  selector: string;
  tag: string;
  text: string | null;
  rect: { x: number; y: number; w: number; h: number };
  viewport: { w: number; h: number };
  dpr: number;
  theme: string | null;
  ua: string;
  version: string;
  at: string;
}

// The codebase uses disciplined per-component class prefixes — a tiny map names the component
// without stamping every file. `data-c` on an ancestor always wins (for new/ambiguous surfaces).
const CLASS_PREFIXES: [string, string][] = [
  ["rm-", "ErasRoad"],
  ["tl-", "TimelinePanel"],
  ["graph-", "GraphPanel"],
  ["lens-", "LensGrids"],
  ["ask-", "ChatPanel"],
  ["journey-", "JourneyPanel"],
  ["jfb-", "Feedback"],
  ["fb-", "Feedback"],
  ["carousel", "Carousel"],
  ["topbar", "Topbar"],
  ["nav", "Topbar"],
  ["footer", "Footer"],
  ["hero", "Hero"],
  ["cfg-", "ConfigManagement"],
  ["crumbs", "Breadcrumb"],
];

function componentFor(el: Element): string | null {
  for (let cur: Element | null = el; cur && cur !== document.body; cur = cur.parentElement) {
    const stamped = cur.getAttribute("data-c");
    if (stamped) return stamped;
    for (const cls of cur.classList) {
      const hit = CLASS_PREFIXES.find(([p]) => cls === p || cls.startsWith(p));
      if (hit) return hit[1];
    }
  }
  return null;
}

function cssPath(el: Element): string {
  const parts: string[] = [];
  let cur: Element | null = el;
  while (cur && cur !== document.body && parts.length < 5) {
    if (cur.id) {
      parts.unshift(`#${cur.id}`);
      break;
    }
    const cls = [...cur.classList].find((c) => !c.includes(":"));
    let part = cur.tagName.toLowerCase() + (cls ? `.${cls}` : "");
    const parent = cur.parentElement;
    if (parent) {
      const same = [...parent.children].filter((c) => c.tagName === cur!.tagName);
      if (same.length > 1) part += `:nth-of-type(${same.indexOf(cur) + 1})`;
    }
    parts.unshift(part);
    cur = cur.parentElement;
  }
  return parts.join(" > ");
}

// Route without single-use secrets: ?qlink / ?mlink carry live magic-link tokens and must never
// land in a feedback record.
function safeRoute(): string {
  const params = new URLSearchParams(location.search);
  params.delete("qlink");
  params.delete("mlink");
  const q = params.toString();
  return location.pathname + (q ? `?${q}` : "");
}

export function buildElementContext(el: Element): ElementContext {
  const r = el.getBoundingClientRect();
  return {
    route: safeRoute(),
    component: componentFor(el),
    selector: cssPath(el),
    tag: el.tagName.toLowerCase(),
    text: (el.textContent ?? "").trim().slice(0, 80) || null,
    rect: { x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.width), h: Math.round(r.height) },
    viewport: { w: window.innerWidth, h: window.innerHeight },
    dpr: window.devicePixelRatio,
    theme: document.documentElement.getAttribute("data-theme"),
    ua: navigator.userAgent,
    version: __APP_VERSION__,
    at: new Date().toISOString(),
  };
}

/** Short human label for the hover chip: `GraphPanel · button.icon-btn`. */
function describe(el: Element): string {
  const comp = componentFor(el);
  const cls = [...el.classList].find((c) => !c.includes(":"));
  const self = el.tagName.toLowerCase() + (cls ? `.${cls}` : "");
  return comp ? `${comp} · ${self}` : self;
}

/**
 * Enter pick mode: crosshair cursor, hover outline + identity label, click/tap captures — and
 * the page's own interactions are frozen for the duration (mouse-family events are suppressed at
 * document capture so drag-starts and close-on-outside handlers can't mutate the page mid-pick;
 * touch keeps its defaults so the visitor can still scroll to the element, and a tap with <10px
 * of travel picks). Esc or the Cancel bar exits. Resolves with the captured context or null.
 */
let busyUntil = 0;
/** True while a pick session is active (plus a grace window for the trailing synthetic click) —
 *  lets the bug-report mode's ⌥-click listener stand down instead of double-pinning. */
export function pickerBusy(): boolean {
  return Date.now() < busyUntil;
}

export function pickElement(): Promise<ElementContext | null> {
  busyUntil = Number.MAX_SAFE_INTEGER;
  return new Promise((resolve) => {
    const outline = document.createElement("div");
    outline.className = "bugpick-outline";
    const label = document.createElement("div");
    label.className = "bugpick-label mono";
    const bar = document.createElement("div");
    bar.className = "bugpick-bar mono";
    const barText = document.createElement("span");
    barText.textContent = "Click or tap the element in question";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "bugpick-cancel";
    cancel.textContent = "Cancel (Esc)";
    bar.append(barText, cancel);
    document.body.append(outline, label, bar);
    document.documentElement.classList.add("bugpick-active");

    const ours = (t: EventTarget | null) =>
      t instanceof Element &&
      Boolean(t.closest(".bugpick-bar, .bugmode-tray, .bugmode-badge"));
    let downPos: { x: number; y: number } | null = null;
    let settled = false;

    // Prefer the event's real target; fall back to hit-testing. Keyboard-synthesized clicks can
    // carry (0,0) coordinates — an indeterminate element is treated as "suppress and ignore".
    const targetOf = (e: MouseEvent): Element | null => {
      if (e.target instanceof Element && !ours(e.target)) return e.target;
      const el = document.elementFromPoint(e.clientX, e.clientY);
      return el && !ours(el) ? el : null;
    };

    const onMove = (e: MouseEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el || ours(el)) {
        outline.style.display = "none";
        label.style.display = "none";
        return;
      }
      const r = el.getBoundingClientRect();
      outline.style.cssText = `display:block;left:${r.left}px;top:${r.top}px;width:${r.width}px;height:${r.height}px`;
      label.textContent = describe(el);
      label.style.display = "block";
      const above = r.top > 34;
      label.style.left = `${Math.max(8, r.left)}px`;
      label.style.top = above ? `${r.top - 30}px` : `${r.bottom + 6}px`;
    };

    // Mouse-family suppression: nothing may reach the app between pointerdown and the pick, or
    // outside-click closers (Journey panel, nav dropdown) and drag-starts (graph) mutate the page
    // under the cursor and the captured context lies.
    const suppress = (e: Event) => {
      if (ours(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
    };
    const onPointerDown = (e: PointerEvent) => {
      if (ours(e.target)) return;
      downPos = { x: e.clientX, y: e.clientY };
      if (e.pointerType === "touch") {
        e.stopPropagation(); // app handlers no; native scroll yes
      } else {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const onPointerUp = (e: PointerEvent) => {
      if (ours(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.pointerType === "touch" && downPos) {
        const moved = Math.hypot(e.clientX - downPos.x, e.clientY - downPos.y);
        if (moved > 10) return; // that was a scroll, not a tap
      }
      const el = targetOf(e);
      if (el) finish(buildElementContext(el));
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation(); // don't let the feedback dialog's Esc-close fire
        finish(null);
      }
    };
    // The pick happens on pointerup, but browsers still dispatch a trailing click — swallow it
    // (and any keyboard-synthesized click) so the picked element is never activated. After finish
    // this is strictly ONE-SHOT: the first post-pick click is the trailing synthetic one; eating
    // anything later would swallow the user's next real click (e.g. the form's Send button).
    const clickSuppressor = (e: MouseEvent) => {
      if (ours(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      if (settled) document.removeEventListener("click", clickSuppressor, true);
    };

    const cleanup = () => {
      document.removeEventListener("mousemove", onMove, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("pointerup", onPointerUp, true);
      document.removeEventListener("mousedown", suppress, true);
      document.removeEventListener("mouseup", suppress, true);
      document.removeEventListener("keydown", onKey, true);
      busyUntil = Date.now() + 450;
      // backstop: if no trailing click ever arrives (Esc/cancel exits), drop the suppressor
      window.setTimeout(() => document.removeEventListener("click", clickSuppressor, true), 400);
      outline.remove();
      label.remove();
      bar.remove();
      document.documentElement.classList.remove("bugpick-active");
    };
    const finish = (ctx: ElementContext | null) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(ctx);
    };

    cancel.addEventListener("click", (e) => {
      e.stopPropagation();
      finish(null);
    });
    document.addEventListener("mousemove", onMove, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("pointerup", onPointerUp, true);
    document.addEventListener("mousedown", suppress, true);
    document.addEventListener("mouseup", suppress, true);
    document.addEventListener("click", clickSuppressor, true);
    document.addEventListener("keydown", onKey, true);
  });
}
